<template>
  <flow-layout :progress="99" :time="1" type="checkout">
    <heading level="2">{{ $t('fundingDetails.title') }}</heading>
    <p class="description">{{ $t('fundingDetails.description') }}</p>

    <review-card :editable="false" name="creditLineDetails" class="creditDetails">
      <template #title>
        {{ translate('creditLineDetails.title') }}
      </template>
      <div class="info">
        <div class="info-cell">
          <div class="label">
            {{ translate('creditLineDetails.creditLimit.label') }}
            <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
              {{ translate('creditLineDetails.creditLimit.tooltip') }}
            </popover>
          </div>
          <div class="value">{{ formatMoney(loanAmount, 2) }}</div>
        </div>

        <div class="info-cell">
          <div class="label">{{ translate('creditLineDetails.loanTermLabel') }}</div>
          <div class="value">
            {{ translate('creditLineDetails.loanTermValue', { loanTerm: termMonths }) }}
          </div>
        </div>

        <div class="info-cell">
          <div class="label">
            {{ translate('creditLineDetails.totalInterest.label') }}
            <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
              {{ translate('creditLineDetails.totalInterest.tooltip') }}
            </popover>
          </div>
          <div class="value">{{ formatMoney(totalInterest, 2) }}</div>
        </div>

        <div class="info-cell">
          <div class="label">{{ translate('creditLineDetails.originationFeeLabel') }}</div>
          <div class="value">{{ formatMoney(originationFee, 2) }}</div>
        </div>

        <div class="info-cell">
          <div class="label">
            {{ translate('creditLineDetails.totalLoanCost.label') }}
            <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
              {{ translate('creditLineDetails.totalLoanCost.tooltip') }}
            </popover>
          </div>
          <div class="value">{{ formatMoney(totalRepaymentAmount, 2) }}</div>
        </div>

        <div class="info-cell">
          <div class="label">{{ periodLabel }}</div>
          <div class="value">{{ formatMoney(payment, 2) }}</div>
        </div>
        <template v-if="isRenewalType">
          <div class="info-cell">
            <div class="label">
              {{ translate('creditLineDetails.renewalPayoffAmountLabel.label') }}
              <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                {{ translate('creditLineDetails.renewalPayoffAmountLabel.tooltip') }}
              </popover>
            </div>
            <div class="value">{{ formatMoney(renewalPayOffAmount, 2) }}</div>
          </div>
        </template>
        <template v-if="isSplitDisbursement || isRenewalType">
          <div class="info-cell">
            <div class="label">
              {{ translate('creditLineDetails.disbursementAmountField.label') }}
              <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                {{ translate('creditLineDetails.disbursementAmountField.tooltip') }}
              </popover>
            </div>
            <div class="value">{{ formatMoney(disbursementAmount, 2) }}</div>
          </div>
          <div class="info-cell">
            <div class="label">
              {{ translate('creditLineDetails.netFundedAmountField.label') }}
              <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                {{ translate('creditLineDetails.netFundedAmountField.tooltip') }}
              </popover>
            </div>
            <div class="value">{{ formatMoney(netFundedAmount, 2) }}</div>
          </div>
        </template>
        <template v-if="isSplitDisbursement">
          <div class="info-cell">
            <div class="label">
              {{ translate('creditLineDetails.splitDisbursementPayOffAmountField.label') }}
              <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                {{ translate('creditLineDetails.splitDisbursementPayOffAmountField.tooltip') }}
              </popover>
            </div>
            <div class="value">{{ formatMoney(splitDisbursementPayOffAmount, 2) }}</div>
          </div>
        </template>
      </div>
    </review-card>

    <review-card :editable="false" name="businessBankAccount" class="businessBankAccount">
      <template #title>
        {{ translate('businessBankAccount.title') }}
      </template>
      <div class="info">
        <text-object :label="translate('businessBankAccount.bankNameLabel')">
          {{ application.bankName }}
        </text-object>
        <text-object :label="translate('businessBankAccount.bsbNumberLabel')">
          {{ application.bsbNumber }}
        </text-object>
        <text-object :label="translate('businessBankAccount.accountNumberLabel')">
          {{ application.bankAccountNumber }}
        </text-object>
      </div>
    </review-card>

    <navigation-buttons @back="goBack" @next="onNextClick" :next="nextButtonFn" />
  </flow-layout>
</template>
<script>
import { mapGetters } from 'vuex';
import FlowLayout from '@/views/layouts/FlowLayout';
import ReviewCard from '@/components/ReviewCard';
import TextObject from '@/components/TextObject';
import percent from '@/filters/percent';
import money from '@/filters/money';
import buildTranslators from '@/utils/build-translators';
import applicationMixin from '@/mixins/application';
import { WEEKLY } from '@/modules/payment-frequencies';
import { agreementAccepted, getOfferPaymentInfo, resetCheckout } from '@/modules/api-service/v3';

export default {
  components: {
    FlowLayout,
    ReviewCard,
    TextObject,
  },
  data() {
    return {
      loanAmount: 0,
      totalInterest: 0,
      totalRepaymentAmount: 0,
      termMonths: 0,
      originationFee: 0,
      payment: 0,
    };
  },
  computed: {
    ...mapGetters({
      offers: 'offer/getOffers',
    }),
    application() {
      return this.$store.getters['application/getApplication']();
    },
    isSplitDisbursement() {
      return !!this.splitDisbursementPayOffAmount;
    },
    disbursementAmount() {
      return Number(this.offer.line || 0);
    },
    splitDisbursementPayOffAmount() {
      return Number(this.offer?.splitDisbursementPayOffAmount || 0);
    },
    renewalPayOffAmount() {
      return this.isRenewalType ? Number(this.offer?.payOffAmount || 0) : 0;
    },
    isRenewalType() {
      return !!this.offer.productId.includes('RENEWALS');
    },
    netFundedAmount() {
      return (
        this.disbursementAmount - this.renewalPayOffAmount - this.splitDisbursementPayOffAmount
      );
    },
    offer() {
      return this.offers.find(offer => offer.accepted);
    },
    decisionId() {
      return this.$store.getters['offer/getDecisionId'];
    },
    periodLabel() {
      const label =
        this.offer.paymentDetails.paymentFrequency === WEEKLY
          ? 'weeklyPaymentLabel'
          : 'dailyPaymentLabel';
      return this.translate(`creditLineDetails.${label}`);
    },
    nextButtonFn() {
      const nextButton = {
        label: this.$t('fundingDetails.nextButtonLabel'),
        modifiers: { disabled: !this.application.bankAccountNumber },
      };
      return nextButton;
    },
  },
  methods: {
    translate: buildTranslators('fundingDetails').t,
    formatMoney: money,
    formatPercent: percent,
    async onNextClick() {
      this.$overlay.show();
      const {
        data: { embeddedUrl },
      } = await agreementAccepted(this.application.id);

      window.location.assign(embeddedUrl);
    },
    async goBack() {
      this.$overlay.show();
      await resetCheckout(this.application.id, this.decisionId);
      await this.$store.dispatch('application/fetchApplication');
      this.$router.replace({ name: `${this.$route.meta.product}Root` }).catch(e => e);
    },
  },
  async created() {
    const { data: acceptedOffer } = await getOfferPaymentInfo(this.application.id, this.offer.id);

    const { interestExpected } = acceptedOffer.total;
    const firstPaymentAmount = acceptedOffer.payments[0].totalDue;
    const { line, paymentDetails, termLoan } = this.offer;

    this.originationFee = Number(line || 0) * Number(paymentDetails.originationFee || 0);
    this.loanAmount = Number(line || 0) + this.originationFee;
    this.totalInterest = Number(interestExpected || 0);
    this.totalRepaymentAmount = this.loanAmount + this.totalInterest;
    this.termMonths = termLoan.termMonths;
    this.payment = firstPaymentAmount;
  },
  mixins: [applicationMixin],
};
</script>
<style lang="sass" scoped>
.description
  color: $color-system-neutral-gray-5
  +font-size(13.33, 24)
  margin: $glass-spacing-1 0 $glass-spacing-4

.creditDetails,
.businessBankAccount
  height: auto
  margin: $glass-spacing-2 0

.info
  display: grid
  row-gap: $glass-spacing-3
  column-gap: $glass-spacing-3
  +mq($stego)
    grid-template-columns: repeat(2, minmax(0, 1fr))
  .label
    +font-size(13.33)
    color: $color-system-neutral-gray-5
  .value
    +font-size(16)
    line-height: 1.2
    color: $color-system-neutral-gray-6

.businessBankAccount
  .info
    +mq($stego)
      grid-template-columns: repeat(3, minmax(0, 1fr))

.navigationButtons
  margin-top: $glass-spacing-3

.popover
  vertical-align: super
  font-weight: normal
  ::v-deep
    .icon
      color: $glass-color-brand-primary-dark-03
    .content
      background: $glass-color-brand-secondary-accent-01
      color: #000000
      z-index: 2
    .iconButton
      &:focus
        outline: 0
        box-shadow: 0 0 0 4px $glass-color-brand-primary-light-02
</style>
